import { noop } from "lodash-es";
import { createPinia } from "pinia";
import "the-new-css-reset/css/reset.css";
import type { App } from "vue";
import { createApp } from "vue";

import AppBase from "@/components/AppBase/AppBase.vue";
import type { IntercomStatic } from "@/declare/windowIntercom";
import { autofocus } from "@/directives/autofocus";
import { boardRegion } from "@/directives/boardRegion";
import { focusTrap } from "@/directives/focusTrap";
import { keyboardNavigation } from "@/directives/keyboardNavigation";
import { ownedCards } from "@/directives/ownedCards";
import { scrollableOnBoard } from "@/directives/scrollableOnBoard";
import { handleError } from "@/error/errorHandler";
import { captureException, initConsoleSentry, initRealSentry } from "@/error/sentry";
import { setLanguage } from "@/language";
import { router } from "@/router/router";
import { assignShortcuts } from "@/shortcuts";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useConnectionStore } from "@/store/connection";
import { useOnboardingStore } from "@/store/onboarding";
import { initSearchBoardSync } from "@/store/search/searchBoardSync";
import "@/styles/global/root-theme.scss";
import { initSearchUrlSync } from "@/store/search/searchUrlSync";
import { startStatistics } from "@/store/statistics";
import { storesPlugin } from "@/storesPlugin";
import { i18n } from "@/translations/i18n";
import {
  disableDefaultContextMenu,
  disableImageDragging,
  preventSpacebarClickInterference,
} from "@/utils/dom/globalEvents";
import { wrapMutationObserver } from "@/utils/dom/mutationObserver";
import { environment, isCloud, sentryDsn, sentryLogBreadcrumbs } from "@/utils/env/Environment";
import { broadcastFeatures } from "@/utils/env/feature";

import { showLogo } from "./console";
import { registerCopyPaste } from "./copyPaste";
import { registerGlobalActions } from "./globalActions";

wrapMutationObserver();

broadcastFeatures();

const app = createApp(AppBase)
  .use(router)
  .use(createPinia().use(storesPlugin))
  .use(i18n)
  .directive("focus-trap", focusTrap())
  .directive("autofocus", autofocus())
  .directive("scrollable-on-board", scrollableOnBoard())
  .directive("owned-cards", ownedCards())
  .directive("keyboard-nav", keyboardNavigation())
  .directive("board-region", boardRegion());

void (
  isCloud && sentryDsn
    ? initRealSentry(app, sentryDsn, environment)
    : initConsoleSentry(sentryLogBreadcrumbs)
).finally(() => {
  initErrorListeners(app);
  app.mount("#app").$watch("$route.query.lang", setLanguage);
});

useClientSettingsStore().init();
initConnectionListeners();

// real Intercom will be inited after login if enabled
// dummy init here, so we can just call Intercom without always checking if it's available or not
window.Intercom = noop as IntercomStatic;

// Debug helper to reset onboarding state — available in dev/staging, stripped from production
if (environment !== "production") {
  window.__resetOnboarding = () => useOnboardingStore().reset();
}

showLogo();
assignShortcuts();
registerGlobalActions();
registerCopyPaste();
preventSpacebarClickInterference();
disableDefaultContextMenu();
disableImageDragging();
startStatistics();
initSearchUrlSync();
initSearchBoardSync();

function initErrorListeners(app: App) {
  window.onunhandledrejection = (event) => {
    event.preventDefault();
    handleError(event.reason);
  };
  window.onerror = (_msg, _url, _line, _col, error) => {
    captureException(error);
    return true;
  };
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    handleError(event.payload);
  });
  app.config.errorHandler = (err) => {
    handleError(err);
  };
  app.config.warnHandler = (err) => {
    handleError(err);
  };
}

function initConnectionListeners() {
  window.addEventListener("offline", () => {
    useConnectionStore().setOffline();
  });
  window.addEventListener("online", () => {
    useConnectionStore().setOnline();
  });
  document.addEventListener("freeze", () => {
    useConnectionStore().freeze();
  });
  document.addEventListener("resume", () => {
    useConnectionStore().unfreeze();
  });
}
